import { useState, type FormEvent } from "react";
import { deleteDoc, doc, updateDoc } from "firebase/firestore";
import type { User } from "firebase/auth";
import { expandLotRange, lotKey, paths, type Job, type Lot } from "@clevis/shared-types";
import { Button, Input, Label, Select } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { editStamp } from "../lib/editStamp";
import { call } from "../lib/callFunction";
import { resolveLot, type LotRow } from "../lib/lots";
import { AddressField, locatedOf, type Located } from "./AddressField";
import type { ProjectItemRow } from "./ProjectItems";

// The pieces of a project's "Lots & jobs" view: adding lots (one or a whole
// release, "1-40"), turning old lot text on jobs into lot records, and one
// lot's details (street address once there is one, plan / elevation, the
// homeowner once it sells).

type JobRow = Job & { id: string };

/** Add one lot or a range ("26", "1-40, 52, 60A"), skipping lots already here. */
export function AddLots({ tenantId, projectId, lots, items }: { tenantId: string; projectId: string; lots: LotRow[]; items: ProjectItemRow[] }) {
  const [text, setText] = useState("");
  const [itemId, setItemId] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const labels = expandLotRange(text);
  const known = new Set(lots.map((l) => l.key));
  const fresh = labels.filter((l) => !known.has(lotKey(l)));

  async function add(e: FormEvent) {
    e.preventDefault();
    if (fresh.length === 0) return;
    setBusy(true);
    setMsg("");
    const item = items.find((i) => i.id === itemId);
    try {
      for (const label of fresh) await resolveLot(tenantId, projectId, label, lots, { projectItemId: item?.id ?? null, projectItemName: item?.name ?? "" });
      const skipped = labels.length - fresh.length;
      setMsg(`Added ${fresh.length} lot${fresh.length === 1 ? "" : "s"}${skipped ? ` (${skipped} already here)` : ""}.`);
      setText("");
    } catch (err) {
      setMsg((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={add} className="flex flex-wrap items-end gap-2 border-b border-steel/10 bg-cream/40 px-4 py-2">
      <div className="min-w-40 flex-1">
        <Label>Add lots</Label>
        <Input className="py-1 text-sm" value={text} placeholder="26, or a release: 1-40, 52, 60A" maxLength={400} onChange={(e) => setText(e.target.value)} />
      </div>
      {items.length > 0 ? (
        <div>
          <Label>Plan / elevation</Label>
          <Select className="w-44 py-1 text-xs" value={itemId} onChange={(e) => setItemId(e.target.value)}>
            <option value="">— later —</option>
            {items.filter((i) => i.status !== "archived").map((i) => (
              <option key={i.id} value={i.id}>
                {i.name}
              </option>
            ))}
          </Select>
        </div>
      ) : null}
      <Button type="submit" className="px-3 py-1 text-xs" busy={busy} disabled={fresh.length === 0}>
        {fresh.length > 1 ? `Add ${fresh.length} lots` : "Add"}
      </Button>
      {text.trim() && labels.length > 0 && fresh.length === 0 ? <p className="w-full text-xs text-steel">Those lots are already here.</p> : null}
      {msg ? <p className="w-full text-xs text-steel">{msg}</p> : null}
    </form>
  );
}

/** Jobs that name a lot only as text: offer to make the lot records and link them. */
export function LinkLotsBanner({ tenantId, projectId, count }: { tenantId: string; projectId: string; count: number }) {
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  if (count === 0 && !msg) return null;
  async function sync() {
    setBusy(true);
    setMsg("");
    try {
      const r = await call<{ created: number; linked: number }>("syncProjectLots", { tenantId, projectId }, { timeoutMs: 300_000 });
      setMsg(`Linked ${r.linked} job${r.linked === 1 ? "" : "s"}${r.created ? `, made ${r.created} new lot${r.created === 1 ? "" : "s"}` : ""}.`);
    } catch (err) {
      setMsg((err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-b border-steel/10 bg-brandorange/10 px-4 py-2 text-xs text-ink">
      {msg ? (
        <span>{msg}</span>
      ) : (
        <>
          <span>
            {count} job{count === 1 ? " names a lot" : "s name lots"} that {count === 1 ? "isn't a lot record" : "aren't lot records"} yet. Linking them puts each lot's install, punch and warranty work
            together and keeps its address and homeowner in one place.
          </span>
          <Button className="px-3 py-1 text-xs" busy={busy} onClick={() => void sync()}>
            Make lot records
          </Button>
        </>
      )}
    </div>
  );
}

/** One lot's details, editable by the office; a manager can remove a lot with no jobs. */
export function LotDetails({
  tenantId,
  projectId,
  lot,
  jobs,
  items,
  canEdit,
  canRemove,
  user,
}: {
  tenantId: string;
  projectId: string;
  lot: LotRow;
  jobs: JobRow[];
  items: ProjectItemRow[];
  canEdit: boolean;
  canRemove: boolean;
  user: User | null;
}) {
  const initial = {
    address: lot.address,
    projectItemId: lot.projectItemId ?? "",
    homeownerName: lot.homeownerName,
    homeownerPhone: lot.homeownerPhone,
    homeownerEmail: lot.homeownerEmail,
    note: lot.note,
    status: lot.status,
  };
  const [f, setF] = useState(initial);
  const [loc, setLoc] = useState<Located>(() => locatedOf(lot));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [confirmRemove, setConfirmRemove] = useState(false);
  const moved = (loc?.lat ?? null) !== (lot.lat ?? null) || (loc?.lng ?? null) !== (lot.lng ?? null);
  const dirty = JSON.stringify(f) !== JSON.stringify(initial) || moved;

  async function save(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMsg("");
    try {
      const item = items.find((i) => i.id === f.projectItemId);
      const address = f.address.trim();
      const patch: Partial<Lot> = {
        address,
        projectItemId: item?.id ?? null,
        projectItemName: item?.name ?? "",
        homeownerName: f.homeownerName.trim(),
        homeownerPhone: f.homeownerPhone.trim(),
        homeownerEmail: f.homeownerEmail.trim(),
        note: f.note.trim(),
        status: f.status,
        lat: loc?.lat ?? null,
        lng: loc?.lng ?? null,
        updatedAt: Date.now(),
      };
      await updateDoc(doc(db, paths.tenantProjectLot(tenantId, projectId, lot.id)), patch);
      // The lot just got its street address or a pin: give it to its open jobs that had none.
      let filled = 0;
      if ((address && address !== lot.address) || (loc && moved)) {
        for (const j of jobs.filter((x) => (x.status === "scheduled" || x.status === "in_progress") && (!x.address.trim() || (loc && x.lat == null)))) {
          await updateDoc(doc(db, paths.tenantJob(tenantId, j.id)), {
            ...(j.address.trim() ? {} : { address }),
            ...(loc ? { lat: loc.lat, lng: loc.lng } : { lat: null, lng: null, geocodeTriedAt: null }),
            ...editStamp(user),
          });
          filled++;
        }
      }
      setMsg(filled ? `Saved. ${filled} open job${filled === 1 ? "" : "s"} got the ${address ? "address" : "pin"}.` : "Saved.");
    } catch (err) {
      setMsg((err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  async function remove() {
    try {
      await deleteDoc(doc(db, paths.tenantProjectLot(tenantId, projectId, lot.id)));
    } catch (err) {
      setMsg((err as Error).message);
    }
  }

  const field = (k: keyof typeof f, label: string, props: Record<string, unknown> = {}) => (
    <div>
      <Label>{label}</Label>
      <Input className="py-1 text-sm" value={f[k]} disabled={!canEdit} onChange={(e) => setF((p) => ({ ...p, [k]: e.target.value }))} {...props} />
    </div>
  );

  return (
    <form onSubmit={save} className="grid gap-2 px-6 py-2 sm:grid-cols-3">
      <div className="sm:col-span-2">
        <Label>Street address (once there is one)</Label>
        {canEdit ? (
          <AddressField value={f.address} onChange={(address) => setF((p) => ({ ...p, address }))} located={loc} onLocate={setLoc} placeholder="e.g. 1234 W Desert Sky Ln, or drop a pin" inputClassName="py-1 text-sm" />
        ) : (
          <Input className="py-1 text-sm" value={f.address} disabled />
        )}
      </div>
      <div>
        <Label>Plan / elevation</Label>
        <Select className="py-1 text-sm" value={f.projectItemId} disabled={!canEdit || items.length === 0} onChange={(e) => setF((p) => ({ ...p, projectItemId: e.target.value }))}>
          <option value="">{items.length ? "— not set —" : "No project items"}</option>
          {items.map((i) => (
            <option key={i.id} value={i.id}>
              {i.name}
            </option>
          ))}
        </Select>
      </div>
      {field("homeownerName", "Homeowner (once sold)", { maxLength: 120 })}
      {field("homeownerPhone", "Homeowner phone", { type: "tel", maxLength: 40 })}
      {lot.sms?.optedOutAt ? <p className="text-[11px] text-danger sm:col-span-2">The homeowner replied STOP: no texts until they text START.</p> : null}
      {field("homeownerEmail", "Homeowner email", { type: "email", maxLength: 200 })}
      <div className="sm:col-span-2">{field("note", "Lot notes (gate code, super, …)", { maxLength: 2000 })}</div>
      <div>
        <Label>Lot</Label>
        <Select className="py-1 text-sm" value={f.status} disabled={!canEdit} onChange={(e) => setF((p) => ({ ...p, status: e.target.value as Lot["status"] }))}>
          <option value="open">Open</option>
          <option value="closed">Closed out</option>
        </Select>
      </div>
      {canEdit ? (
        <div className="flex flex-wrap items-center gap-2 sm:col-span-3">
          <Button type="submit" className="px-3 py-1 text-xs" busy={busy} disabled={!dirty}>
            Save lot
          </Button>
          {msg ? <span className="text-xs text-steel">{msg}</span> : null}
          {canRemove && jobs.length === 0 ? (
            confirmRemove ? (
              <span className="ml-auto flex items-center gap-2 text-xs">
                Remove {lot.name}?
                <button type="button" className="font-semibold text-danger hover:underline" onClick={() => void remove()}>
                  Remove
                </button>
                <button type="button" className="text-steel hover:text-ink" onClick={() => setConfirmRemove(false)}>
                  Keep
                </button>
              </span>
            ) : (
              <button type="button" className="ml-auto text-xs text-steel hover:text-danger" onClick={() => setConfirmRemove(true)}>
                Remove lot
              </button>
            )
          ) : null}
        </div>
      ) : null}
    </form>
  );
}
